import React, { useState, useEffect, useCallback } from 'react';
import {
  FaTimes,
  FaExclamationTriangle,
  FaSync,
  FaUserCheck,
  FaCheckCircle,
  FaSpinner,
} from 'react-icons/fa';
import {
  listFranchiseeDuplicates,
  mergeFranchisees,
  previewMergeFranchisees,
  scanAndAlertFranchiseeDuplicates,
} from '../services/franchiseeDuplicateService';

const DuplicateFranchiseesModal = ({ isOpen, onClose, onResolved }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  // Index of group currently being merged (null when idle)
  const [mergingGroup, setMergingGroup] = useState(null);
  const [mergeProgress, setMergeProgress] = useState(null);
  // Pending confirmation: { groupIndex, keep, others: [...] }
  const [pendingMerge, setPendingMerge] = useState(null);
  // Read-only preview of the pending merge's impact (aggregated across all "others").
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  // Result of the most recent merge, shown as a banner. Not keyed to a group index,
  // because the resolved group disappears from the list after the post-merge refresh.
  const [mergeResult, setMergeResult] = useState(null);

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listFranchiseeDuplicates();
      setGroups(data?.groups || []);
    } catch (err) {
      console.error('Error fetching franchisee duplicates:', err);
      setError(err.message || 'Failed to load duplicate franchisees.');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setScanResult(null);
      setPendingMerge(null);
      setPreviewData(null);
      setPreviewError(null);
      setPreviewLoading(false);
      setMergeResult(null);
      setMergeProgress(null);
      fetchDuplicates();
    }
  }, [isOpen, fetchDuplicates]);

  if (!isOpen) return null;

  // A merge or scan anywhere in the modal disables all merge entry points, so two
  // merges (and their racy refreshes) can never overlap.
  const isBusy = mergingGroup !== null || !!pendingMerge || scanning || loading;

  const handleScanAndAlert = async () => {
    setScanning(true);
    setScanResult(null);
    setError(null);
    try {
      const data = await scanAndAlertFranchiseeDuplicates();
      setScanResult(data);
      // Refresh the list to reflect the latest scan.
      await fetchDuplicates();
    } catch (err) {
      console.error('Error scanning for duplicate franchisees:', err);
      setError(err.message || 'Failed to re-scan for duplicates.');
    } finally {
      setScanning(false);
    }
  };

  const requestKeep = async (groupIndex, group, keep) => {
    const others = (group.franchisees || []).filter((f) => f.id !== keep.id);
    setPendingMerge({ groupIndex, keep, others });
    setPreviewData(null);
    setPreviewError(null);

    if (others.length === 0) return;

    setPreviewLoading(true);
    try {
      // Preview each keep->other merge independently, then aggregate into a single
      // "combined effect" summary. For a 2-record group this is one call.
      const previews = await Promise.all(
        others.map((other) => previewMergeFranchisees(keep.id, other.id))
      );

      const aggregated = {
        willMove: { jobs: 0, schedules: 0, users: 0 },
        preservedFields: [],
        fseCodeChange: null,
        warnings: [],
      };
      const seenFields = new Set();
      const seenWarnings = new Set();

      previews.forEach((preview) => {
        const move = preview?.willMove || {};
        aggregated.willMove.jobs += move.jobs || 0;
        aggregated.willMove.schedules += move.schedules || 0;
        aggregated.willMove.users += move.users || 0;

        if (Array.isArray(preview?.preservedFields)) {
          preview.preservedFields.forEach((pf) => {
            if (pf && pf.field && !seenFields.has(pf.field)) {
              seenFields.add(pf.field);
              aggregated.preservedFields.push(pf);
            }
          });
        }

        if (!aggregated.fseCodeChange && preview?.fseCodeChange) {
          aggregated.fseCodeChange = preview.fseCodeChange;
        }

        if (Array.isArray(preview?.warnings)) {
          preview.warnings.forEach((w) => {
            if (w && !seenWarnings.has(w)) {
              seenWarnings.add(w);
              aggregated.warnings.push(w);
            }
          });
        }
      });

      setPreviewData(aggregated);
    } catch (err) {
      console.error('Error previewing franchisee merge:', err);
      setPreviewError(err.message || 'Failed to preview merge impact.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const cancelPendingMerge = () => {
    setPendingMerge(null);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(false);
  };

  const confirmMerge = async () => {
    if (!pendingMerge) return;
    const { groupIndex, keep, others } = pendingMerge;
    setPendingMerge(null);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setMergingGroup(groupIndex);
    setError(null);
    setMergeProgress(null);
    setMergeResult(null);

    const totals = {
      movedJobs: 0,
      movedSchedules: 0,
      repointedUsers: 0,
      preservedFields: [],
      mergedCount: 0,
    };
    let mergeError = null;

    for (let i = 0; i < others.length; i += 1) {
      const other = others[i];
      if (others.length > 1) {
        setMergeProgress(`Merging record ${i + 1} of ${others.length}...`);
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await mergeFranchisees(keep.id, other.id);
        totals.movedJobs += result?.movedJobs || 0;
        totals.movedSchedules += result?.movedSchedules || 0;
        totals.repointedUsers += result?.repointedUsers || 0;
        if (Array.isArray(result?.preservedFields)) {
          result.preservedFields.forEach((field) => {
            if (!totals.preservedFields.includes(field)) {
              totals.preservedFields.push(field);
            }
          });
        }
        totals.mergedCount += 1;
      } catch (err) {
        console.error('Error merging franchisees:', err);
        mergeError = err;
        break;
      }
    }

    setMergingGroup(null);
    setMergeProgress(null);

    // If anything merged, surface the result and refresh — even on a partial failure,
    // since some records are already suspended in Firestore and the parent list is stale.
    if (totals.mergedCount > 0) {
      setMergeResult({ keepName: keep.franchiseName, ...totals });
      if (onResolved) {
        onResolved();
      }
      await fetchDuplicates();
    }

    if (mergeError) {
      setError(
        `Failed to merge franchisees${
          totals.mergedCount > 0 ? ` after ${totals.mergedCount} successful merge(s)` : ''
        }: ${mergeError.message}`
      );
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp?.toDate
        ? timestamp.toDate()
        : timestamp?._seconds
        ? new Date(timestamp._seconds * 1000)
        : new Date(timestamp);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return 'N/A';
    }
  };

  const Badge = ({ active, children }) => (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
        active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-400'
      }`}
    >
      {children}
    </span>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-600 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <FaExclamationTriangle className="mr-3 text-2xl" />
            <div>
              <h2 className="text-xl font-bold">Duplicate Franchisees</h2>
              <p className="text-sm text-red-100">
                Records that appear to refer to the same franchisee
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-200 transition-colors"
          >
            <FaTimes className="text-2xl" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            {!loading && (
              <span>
                {groups.length > 0
                  ? `${groups.length} duplicate group(s) found`
                  : 'No duplicate groups'}
              </span>
            )}
          </div>
          <button
            onClick={handleScanAndAlert}
            disabled={isBusy}
            className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-semibold py-2 px-4 rounded inline-flex items-center transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {scanning ? (
              <>
                <FaSpinner className="mr-2 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <FaSync className="mr-2" />
                Re-scan &amp; email admins
              </>
            )}
          </button>
        </div>

        {/* Scan result toast */}
        {scanResult && (
          <div className="mx-6 mt-4 p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded text-sm flex items-start">
            <FaCheckCircle className="mt-0.5 mr-2 flex-shrink-0" />
            <span>
              Scan complete: {scanResult.groupCount} duplicate group(s) found.{' '}
              {scanResult.emailed ? 'Admins were emailed.' : 'No email was sent.'}
            </span>
          </div>
        )}

        {/* Merge result banner */}
        {mergeResult && (
          <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded text-sm">
            <div className="flex items-center font-semibold mb-1">
              <FaCheckCircle className="mr-2 flex-shrink-0" />
              Merged into {mergeResult.keepName || 'kept record'}
            </div>
            <span>
              {mergeResult.movedJobs} job(s), {mergeResult.movedSchedules} schedule(s) and{' '}
              {mergeResult.repointedUsers} login(s) moved
              {mergeResult.preservedFields.length > 0
                ? `; preserved: ${mergeResult.preservedFields.join(', ')}`
                : ''}
              .
            </span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <FaSpinner className="animate-spin text-3xl mx-auto mb-3" />
              <p>Loading duplicate franchisees...</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No duplicate franchisees found</p>
              <p className="text-sm mt-2">All franchisee records appear to be unique</p>
            </div>
          ) : (
            <div className="space-y-6">
              {groups.map((group, groupIndex) => {
                const isMerging = mergingGroup === groupIndex;
                const franchisees = group.franchisees || [];
                const gridColsClass =
                  franchisees.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';

                return (
                  <div
                    key={groupIndex}
                    className="border border-red-200 rounded-lg p-4 bg-red-50"
                  >
                    {/* Confidence + match reasons */}
                    {((group.matchReasons && group.matchReasons.length > 0) ||
                      group.confidence) && (
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        {group.confidence && (
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase ${
                              group.confidence === 'high'
                                ? 'bg-red-600 text-white'
                                : 'bg-amber-200 text-amber-900'
                            }`}
                          >
                            {group.confidence === 'high' ? 'High confidence' : 'Needs review'}
                          </span>
                        )}
                        {group.matchReasons && group.matchReasons.length > 0 && (
                          <>
                            <span className="text-xs font-semibold text-red-700 uppercase">
                              Matched on:
                            </span>
                            {group.matchReasons.map((reason, i) => (
                              <span
                                key={i}
                                className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-200 text-red-800"
                              >
                                {reason}
                              </span>
                            ))}
                          </>
                        )}
                      </div>
                    )}

                    <div className={`grid grid-cols-1 ${gridColsClass} gap-4`}>
                          {franchisees.map((f) => (
                            <div
                              key={f.id}
                              className="bg-white rounded-lg p-4 border border-gray-200 flex flex-col"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <h3 className="font-semibold text-gray-900">
                                  {f.franchiseName || 'Unnamed'}
                                </h3>
                                <span
                                  className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                    f.isActive
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-200 text-gray-600'
                                  }`}
                                >
                                  {f.isActive ? 'Active' : 'Suspended'}
                                </span>
                              </div>

                              <div className="text-sm text-gray-600 space-y-1 mb-3">
                                <p>
                                  <span className="text-gray-400">Code:</span>{' '}
                                  {f.fseCode || 'N/A'}
                                </p>
                                <p>
                                  <span className="text-gray-400">Contact:</span>{' '}
                                  {[f.firstName, f.lastName].filter(Boolean).join(' ') || 'N/A'}
                                </p>
                                <p className="break-all">
                                  <span className="text-gray-400">Email:</span>{' '}
                                  {f.email || 'N/A'}
                                </p>
                                <p>
                                  <span className="text-gray-400">Phone:</span>{' '}
                                  {f.phoneNumber || 'N/A'}
                                </p>
                                <p>
                                  <span className="text-gray-400">ABN:</span> {f.abn || 'N/A'}
                                </p>
                                <p>
                                  <span className="text-gray-400">Territory:</span>{' '}
                                  {f.territory || 'N/A'}
                                </p>
                                <p>
                                  <span className="text-gray-400">Created:</span>{' '}
                                  {formatDate(f.createdAt)}
                                </p>
                              </div>

                              <div className="flex flex-wrap gap-1.5 mb-3">
                                <Badge active={f.hasUser}>Has login</Badge>
                                <Badge active={f.hasXero}>Xero linked</Badge>
                                <Badge active={f.hasCompliance}>Compliance</Badge>
                                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                  {f.jobCount || 0} jobs
                                </span>
                              </div>

                              <button
                                onClick={() => requestKeep(groupIndex, group, f)}
                                disabled={isBusy}
                                className="mt-auto w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-3 rounded inline-flex items-center justify-center transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <FaUserCheck className="mr-2" />
                                Keep this one
                              </button>
                            </div>
                          ))}
                        </div>

                    {isMerging && (
                      <div className="mt-3 flex items-center text-sm text-gray-700">
                        <FaSpinner className="animate-spin mr-2" />
                        {mergeProgress || 'Merging...'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>

      {/* Keep / Merge confirmation overlay */}
      {pendingMerge && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-3">
              <FaExclamationTriangle className="text-yellow-500 text-2xl mr-3" />
              <h3 className="text-lg font-bold text-gray-900">Confirm merge</h3>
            </div>
            <p className="text-sm text-gray-700 mb-4">
              Retain{' '}
              <span className="font-semibold">
                {pendingMerge.keep.franchiseName || 'this franchisee'}
              </span>{' '}
              (<span className="font-mono">{pendingMerge.keep.fseCode || 'N/A'}</span>). Suspend
              the other record(s).
            </p>

            {previewLoading ? (
              <div className="flex items-center text-sm text-gray-600 mb-4">
                <FaSpinner className="animate-spin mr-2" />
                Calculating impact...
              </div>
            ) : previewError ? (
              <div className="mb-4">
                <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
                  {previewError}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  The impact could not be previewed. You may still confirm the merge.
                </p>
              </div>
            ) : previewData ? (
              <div className="mb-4 space-y-3 text-sm text-gray-700">
                <p>
                  Will move:{' '}
                  <span className="font-semibold">{previewData.willMove.jobs}</span> job(s),{' '}
                  <span className="font-semibold">{previewData.willMove.schedules}</span>{' '}
                  schedule(s),{' '}
                  <span className="font-semibold">{previewData.willMove.users}</span> login(s) to
                  the kept record.
                  {pendingMerge.others.length > 1 && (
                    <span className="text-xs text-gray-500"> (combined effect)</span>
                  )}
                </p>

                {previewData.preservedFields.length > 0 && (
                  <div>
                    <p className="font-semibold text-gray-800 mb-1">Preserved onto kept record:</p>
                    <ul className="list-disc list-inside space-y-0.5 text-gray-600">
                      {previewData.preservedFields.map((pf) => (
                        <li key={pf.field}>{pf.note || pf.field}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {previewData.fseCodeChange && (
                  <p>
                    FSE code: kept record adopts{' '}
                    <span className="font-mono font-semibold">
                      {previewData.fseCodeChange.newKeeperFseCode}
                    </span>
                    ; the other becomes{' '}
                    <span className="font-mono">
                      {previewData.fseCodeChange.newLoserFseCode}
                    </span>
                    .
                  </p>
                )}

                {previewData.warnings.length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded">
                    <div className="flex items-center text-amber-800 font-semibold mb-1">
                      <FaExclamationTriangle className="mr-2 flex-shrink-0" />
                      Conflicts
                    </div>
                    <ul className="list-disc list-inside space-y-0.5 text-amber-800">
                      {previewData.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}

            <div className="flex justify-end gap-3">
              <button
                onClick={cancelPendingMerge}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmMerge}
                disabled={previewLoading}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DuplicateFranchiseesModal;
